import React, { useCallback } from "react";
import { useAppSettings } from "../context/AppSettingsContext";
import "../styles/CurrentUserDetails.css";
import "../styles/Header.css";
import UserPhoto from "./shared/UserPhoto";
import UserFullName from "./shared/UserFullName";
import NotificationBellIcon from "./shared/NotificationBellIcon";
import NotificationConfirmationModal from "./Modals/NotificationConfirmationModal";
import { useChatStore } from "../store/useChatStore";
import { useNotificationActions } from "../hooks/useNotificationActions";
// Define memoized components BEFORE using them
const MemoizedUserPhoto = React.memo(UserPhoto);
const MemoizedUserFullName = React.memo(UserFullName);

const Header = React.memo(
    ({ chatClient }) => {
        const isSidebarOpen = useChatStore((s) => s.isSidebarOpen);
        const setIsSidebarOpen = useChatStore((s) => s.setIsSidebarOpen);
        const setSidebarStateChangeKey = useChatStore((s) => s.setSidebarStateChangeKey);

        // Use shared notification hook
        const {
          showNotificationModal,
          loading,
          showNotificationIcon,
          handleNotificationClick,
          handleModalClose,
          getConfirmHandler,
          hasInteractedWithNotifications,
          isNotificationsEnabled,
          getModalTitle,
          getModalMessage,
        } = useNotificationActions();

    // Toggle sidebar
    const toggleSidebar = useCallback(() => {
      setIsSidebarOpen((prev) => !prev);
      setSidebarStateChangeKey((key) => key + 1);
    }, [setIsSidebarOpen, setSidebarStateChangeKey]);

    const { settings } = useAppSettings();
    const colorStyle = settings?.textColor ? { color: settings.textColor } : '#FEFEFE';

    const fcmToken = isNotificationsEnabled();
    const canShowBell = hasInteractedWithNotifications();

    return (
      <header className="header" style={{ backgroundColor: settings.bgColor }}>
        <span
          className={`hamburger-icon${isSidebarOpen ? " open" : ""}`}
          onClick={toggleSidebar}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="28"
            height="28"
            viewBox="0 0 20 21"
            fill="none"
            style={colorStyle}
          >
            <path
              d="M7.5 3V18M11.6667 8L14.1667 10.5L11.6667 13M4.16667 3H15.8333C16.7538 3 17.5 3.74619 17.5 4.66667V16.3333C17.5 17.2538 16.7538 18 15.8333 18H4.16667C3.24619 18 2.5 17.2538 2.5 16.3333V4.66667C2.5 3.74619 3.24619 3 4.16667 3Z"
              stroke="currentColor"
              strokeWidth="1.33"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
        <div className="header-right-section">
          <div
            className="current-user-details"
            style={{ backgroundColor: "transparent" }}
          >
            <MemoizedUserPhoto
              photo={chatClient.user?.photo}
              firstName={chatClient.user?.firstName}
              lastName={chatClient.user?.lastName}
              sharedFirstName={chatClient.user?.sharedFirstName}
              sharedLastName={chatClient.user?.sharedLastName}
            />
            <div className="user-info" style={{ color : settings.textColor }}>
              <MemoizedUserFullName
                name={chatClient.user?.name}
                firstName={chatClient.user?.firstName}
                lastName={chatClient.user?.lastName}
                sharedFirstName={chatClient.user?.sharedFirstName}
                sharedLastName={chatClient.user?.sharedLastName}
                cssClass="loggedin-user-name"
              />
            </div>
          </div>
          {showNotificationIcon && canShowBell && (
            <NotificationBellIcon
              onClick={handleNotificationClick}
              style={colorStyle}
              isEnabled={fcmToken}
              variant="header"
            />
          )}
        </div>
        {/* Pause/Resume Notification Modal */}
        <NotificationConfirmationModal
          isOpen={showNotificationModal}
          toggle={handleModalClose}
          onConfirm={getConfirmHandler()}
          title={getModalTitle()}
          message={getModalMessage()}
          userName={chatClient.user?.name}
          loading={loading}
        />
      </header>
    );
  },
  (prevProps, nextProps) =>
    prevProps.toggleSidebar === nextProps.toggleSidebar &&
    prevProps.chatClient.user?.id === nextProps.chatClient.user?.id
);

Header.displayName = "Header";
export default Header;
